import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@astryxdesign/core/Toast';
import { BellRingingIcon, CalendarPlusIcon, CheckIcon, CopyIcon, NavigationArrowIcon, PhoneIcon } from '@phosphor-icons/react';
import { Notice, PillButton } from '@/components/kit';
import { publicApi } from '@/lib/api/public';
import type { PublicBooking } from '@/lib/api/schemas';
import { errorMessage } from '@/lib/api/errors';
import { env } from '@/lib/env';
import { formatDateTime, formatPhoneHref } from '@/lib/format';
import { buildIcs, downloadIcs } from '@/lib/ics';
import { pushSupport } from '@/lib/pwa/platform';
import { subscribeToPush } from '@/lib/pwa/push';
import { bookingShareLink } from '@/lib/device-bookings';

interface ReminderActionsProps {
  booking: PublicBooking;
  token: string;
}

/**
 * Tiles under an upcoming booking (calendar file, route, call, private link) and the
 * reminder row. States are reported as they are: a downloaded .ics is "saved", not
 * "added to your calendar"; a push reminder is "planned" only once the server has it.
 */
export function ReminderActions({ booking, token }: ReminderActionsProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pushState, setPushState] = useState<'idle' | 'working' | 'error'>('idle');
  const [pushError, setPushError] = useState<string | null>(null);
  const support = pushSupport(env.VITE_VAPID_PUBLIC_KEY);
  const tz = booking.studio.timezone;
  const reminder = booking.reminder;
  if (booking.status !== 'confirmed' && booking.status !== 'arrived') return null;
  const upcoming = booking.status === 'confirmed';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingShareLink(booking.studio.slug, token));
      toast({ body: 'Ссылка на запись скопирована. Не пересылайте её посторонним — по ней можно отменить запись.' });
    } catch {
      toast({ body: 'Не удалось скопировать ссылку', type: 'error' });
    }
  };

  const enablePush = async () => {
    setPushState('working');
    setPushError(null);
    try {
      const subscription = await subscribeToPush();
      await publicApi.registerPush(booking.studio.slug, token, subscription);
      await queryClient.invalidateQueries({ queryKey: ['my-bookings', booking.studio.slug] });
      setPushState('idle');
    } catch (error) {
      setPushState('error');
      setPushError(error instanceof Error && !('code' in error) ? error.message : errorMessage(error));
    }
  };

  const saveIcs = () => {
    const content = buildIcs({
      uid: `${booking.id}@plansgx`,
      start: booking.startsAt,
      end: booking.endsAt,
      title: `${booking.service.name} — ${booking.studio.name}`,
      location: booking.studio.address,
      description: `Запись ${booking.code}. Телефон студии: ${booking.studio.phone}`,
      url: bookingShareLink(booking.studio.slug, token),
    });
    downloadIcs(`zapis-${booking.code}.ics`, content);
    toast({ body: 'Файл календаря сохранён. Откройте его, чтобы добавить событие с напоминанием за сутки.' });
  };

  const planned = reminder.status === 'pending' || reminder.status === 'processing';
  const canPush =
    !booking.isDemo && support.kind === 'supported' && !planned && reminder.status !== 'sent' && reminder.status !== 'too_late';

  let status = 'Push на этот телефон перед визитом.';
  if (booking.isDemo) status = 'В образце студии напоминания не отправляются.';
  else if (planned) status = `Придёт ${reminder.runAt ? formatDateTime(reminder.runAt, tz) : 'заранее'} на это устройство.`;
  else if (reminder.status === 'sent') status = 'Напоминание отправлено.';
  else if (reminder.status === 'too_late') status = 'До визита меньше двух часов — напоминание не понадобится.';
  else if (support.kind === 'ios-needs-install')
    status = 'На iPhone работает, когда приложение добавлено на экран «Домой». Пока сохраните запись в календарь.';
  else if (support.kind === 'denied') status = 'Уведомления запрещены в настройках браузера — сохраните запись в календарь.';
  else if (support.kind !== 'supported') status = 'Этот браузер не поддерживает уведомления — сохраните запись в календарь.';

  return (
    <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-2)' }}>
      <div className="ui-tiles">
        {upcoming ? (
          <button type="button" className="ui-tile" onClick={saveIcs}>
            <CalendarPlusIcon size={22} aria-hidden />
            Календарь
          </button>
        ) : null}
        {booking.studio.mapUrl ? (
          <a className="ui-tile" href={booking.studio.mapUrl} target="_blank" rel="noopener noreferrer">
            <NavigationArrowIcon size={22} aria-hidden />
            Маршрут
          </a>
        ) : null}
        <a className="ui-tile" href={formatPhoneHref(booking.studio.phone)}>
          <PhoneIcon size={22} aria-hidden />
          Позвонить
        </a>
        <button type="button" className="ui-tile" onClick={() => void copyLink()}>
          <CopyIcon size={22} aria-hidden />
          Ссылка
        </button>
      </div>

      {upcoming ? (
        <div className="ui-remind">
          <BellRingingIcon size={22} className="ui-list__icon" aria-hidden />
          <span className="ui-stack ui-grow" style={{ ['--ui-gap' as string]: '2px' }}>
            <span className="ui-title">Напоминание</span>
            <span className="ui-caption" role="status">
              {status}
            </span>
          </span>
          {planned || reminder.status === 'sent' ? (
            <span className="ui-remind__on">
              <CheckIcon size={16} weight="bold" aria-hidden />
              Включено
            </span>
          ) : canPush ? (
            <PillButton size="sm" variant="primary" loading={pushState === 'working'} onClick={() => void enablePush()}>
              Включить
            </PillButton>
          ) : null}
        </div>
      ) : null}
      {pushError ? (
        <Notice tone="error" title="Напоминание не включено">
          {pushError}
        </Notice>
      ) : null}
    </div>
  );
}
